import { resolveBloomColor, type BloomColor, type BloomRgb } from "./bloomPalette";

export const PAINT_SIZE = 256;
const TILE = 32, INSET = 2, SPAN = TILE - INSET * 2;
export interface PetalHit {petal:number;u:number;v:number}
export function petalAtlasUv(petal:number,u:number,v:number) {
  return {x:((petal%8)*TILE+INSET+u*SPAN)/PAINT_SIZE,y:(Math.floor(petal/8)*TILE+INSET+v*SPAN)/PAINT_SIZE};
}
export function petalFromAtlasUv(x:number,y:number):PetalHit {
  const col=Math.floor(x*8),row=Math.floor(y*8);
  return {petal:row*8+col,u:(x*PAINT_SIZE-col*TILE-INSET)/SPAN,v:(y*PAINT_SIZE-row*TILE-INSET)/SPAN};
}
interface Dab extends PetalHit {color:BloomRgb;age:number}

/** A small paint atlas, independent of renderer lifetime. Eight undo snapshots cost 2 MiB. */
export function createBloomPainting() {
  const pixels=new Uint8Array(PAINT_SIZE*PAINT_SIZE*4);
  const history:Uint8Array[]=[];
  let snapshot:Uint8Array|null=null, changed=false, version=0;
  const dabs:Dab[]=[];
  const stamp=(dab:Dab,dt:number,target=pixels) => {
    const originX=(dab.petal%8)*TILE,originY=Math.floor(dab.petal/8)*TILE;
    const cx=originX+INSET+dab.u*SPAN,cy=originY+INSET+dab.v*SPAN;
    const radius=2.2+4.3*Math.min(1,dab.age/0.5);
    const color=dab.color;
    for(let y=Math.max(originY,Math.floor(cy-radius));y<=Math.min(originY+TILE-1,Math.ceil(cy+radius));y++) {
      for(let x=Math.max(originX,Math.floor(cx-radius));x<=Math.min(originX+TILE-1,Math.ceil(cx+radius));x++) {
        const distance=Math.hypot(x-cx,y-cy)/radius;
        if(distance>=1)continue;
        const weight=(1-distance*distance)**2;
        const alpha=1-Math.exp(-dt*7*weight),index=(y*PAINT_SIZE+x)*4;
        const oldAlpha=target[index+3]/255,newAlpha=alpha+oldAlpha*(1-alpha);
        for(let c=0;c<3;c++)target[index+c]=Math.round((color[c]*alpha+target[index+c]*oldAlpha*(1-alpha))/newAlpha);
        target[index+3]=Math.round(newAlpha*255);
      }
    }
  };
  const settle=()=>{
    dabs.forEach(dab=>{dab.age=0.6;stamp(dab,0.7);});
    if(dabs.length)version++;
    dabs.length=0;
  };
  return {
    pixels,
    get version(){return version;},
    capture(){
      // Project committed, still-diffusing dabs onto a copy. Never interrupt the live bloom.
      if(snapshot)return snapshot.slice();
      const saved=pixels.slice();
      dabs.forEach(dab=>stamp({...dab,age:0.6},0.7,saved));
      return saved;
    },
    restore(saved:Uint8Array){
      if(saved.length!==pixels.length)return false;
      pixels.set(saved);dabs.length=0;history.length=0;snapshot=null;changed=false;version++;
      return true;
    },
    begin(){settle();snapshot=pixels.slice();changed=false;},
    dab(hit:PetalHit,brush:BloomColor) {
      const color=resolveBloomColor(brush);
      if(!snapshot || !Number.isInteger(hit.petal) || hit.petal<0 || hit.petal>=55 || !Number.isFinite(hit.u+hit.v) || !color)return false;
      // Store the RGB value at contact, never a reference to the active color picker.
      const dab={petal:hit.petal,u:Math.max(0,Math.min(1,hit.u)),v:Math.max(0,Math.min(1,hit.v)),color,age:0};
      const previous=dabs.at(-1);
      if(previous?.petal===dab.petal && previous.color.every((value,index)=>value===color[index]) && Math.hypot(previous.u-dab.u,previous.v-dab.v)<0.055)return false;
      if(dabs.length>=48){const oldest=dabs.shift()!;oldest.age=0.6;stamp(oldest,0.7);}
      dabs.push(dab);changed=true;return true;
    },
    advance(dt:number,reduced:boolean) {
      if(!dabs.length)return false;
      if(reduced){settle();return true;}
      const step=Math.max(0,Math.min(1,dt));
      for(let i=dabs.length-1;i>=0;i--) {
        dabs[i].age+=step;stamp(dabs[i],step);
        if(dabs[i].age>=0.65)dabs.splice(i,1);
      }
      version++;return true;
    },
    end(commit:boolean) {
      if(!snapshot)return false;
      const hasPaint=changed;
      if(!commit){pixels.set(snapshot);dabs.length=0;version++;}
      else if(changed){history.push(snapshot);if(history.length>8)history.shift();}
      snapshot=null;changed=false;return commit&&hasPaint;
    },
    undo(){if(!history.length)return false;dabs.length=0;snapshot=null;pixels.set(history.pop()!);version++;return true;},
    clear(){pixels.fill(0);dabs.length=0;history.length=0;snapshot=null;changed=false;version++;},
    canUndo(){return history.length>0;},
    hasPaint(){return pixels.some((value,index)=>index%4===3&&value>0)||dabs.length>0;},
  };
}
export type BloomPainting=ReturnType<typeof createBloomPainting>;
